import { Component } from '@theme/component';
import { applySwatchBackground, debounce } from '@theme/utilities';

/**
 * A custom element that provides a dialog-based variant selector.
 * Used for dropdown-style variant pickers with enhanced UX.
 *
 * @extends Component
 */
class VariantSelectDialog extends Component {
  /** @type {(() => void) | null} */
  #debouncedResize = null;

  /** @type {(() => void) | null} */
  #debouncedCloseOnResize = null;

  connectedCallback() {
    super.connectedCallback();

    this.dialog?.addEventListener('click', this.#handleBackdropClick);
    this.dialogContent?.addEventListener('click', this.#handleOptionClick);
    this.dialogCloseButton?.addEventListener('click', this.#handleCloseClick);

    this.#debouncedResize = debounce(() => this.#updateDialogPosition(), 100);
    this.#debouncedCloseOnResize = debounce(() => this.closeDialog(), 100);
  }

  updatedCallback() {
    super.updatedCallback?.();
    this.dialog?.removeEventListener('click', this.#handleBackdropClick);
    this.dialogContent?.removeEventListener('click', this.#handleOptionClick);
    this.dialogCloseButton?.removeEventListener('click', this.#handleCloseClick);
    this.dialog?.addEventListener('click', this.#handleBackdropClick);
    this.dialogContent?.addEventListener('click', this.#handleOptionClick);
    this.dialogCloseButton?.addEventListener('click', this.#handleCloseClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.dialog?.removeEventListener('click', this.#handleBackdropClick);
    this.dialogContent?.removeEventListener('click', this.#handleOptionClick);
    this.dialogCloseButton?.removeEventListener('click', this.#handleCloseClick);
    this.#unbindResize();
    this.#unbindResizeClose();
    document.body.style.overflow = '';
  }

  /** @returns {HTMLDialogElement | null} */
  get dialog() {
    return this.querySelector('dialog');
  }

  /** @returns {HTMLElement | null} */
  get triggerValue() {
    return this.querySelector('.variant-select-trigger__value');
  }

  /** @returns {HTMLElement | null} */
  get triggerSwatch() {
    return this.querySelector('.variant-select-trigger__swatch');
  }

  /** @returns {HTMLSelectElement | null} */
  get hiddenSelect() {
    return this.querySelector('select');
  }

  /** @returns {HTMLElement | null} */
  get dialogContent() {
    return this.querySelector('.variant-select-dialog__content');
  }

  /** @returns {HTMLButtonElement | null} */
  get dialogCloseButton() {
    return this.querySelector('.variant-select-dialog__close');
  }

  /**
   * Opens the dialog
   */
  openDialog() {
    this.dialog?.showModal();
    document.body.style.overflow = 'hidden';

    // Rotate caret icon
    this.trigger?.classList.add('is-open');

    // Position dialog above sticky bar if inside one
    this.#positionAboveStickyBar();

    // Close on viewport resize only when inside sticky bar (so dialog does not stay open when bar visibility changes)
    if (this.closest('product-sticky-details')) {
      this.#bindResizeClose();
    }
  }

  /**
   * Closes the dialog
   */
  closeDialog() {
    this.dialog?.close();
    document.body.style.overflow = '';

    // Reset caret icon rotation
    this.trigger?.classList.remove('is-open');

    this.#unbindResizeClose();
    // Reset sticky bar positioning
    this.#resetStickyBarPosition();
  }

  /** @returns {HTMLElement | null} */
  get trigger() {
    return this.querySelector('.variant-select-trigger');
  }

  /**
   * Positions the dialog above the sticky bar if this component is inside one
   */
  #positionAboveStickyBar() {
    const stickyBar = /** @type {HTMLElement | null} */ (this.closest('product-sticky-details'));
    if (!stickyBar || !this.dialog) return;

    this.dialog.dataset.insideStickyBar = 'true';
    this.#updateDialogPosition(stickyBar);
    this.#bindResize();
  }

  /**
   * Updates the dialog position values
   * @param {HTMLElement | null} [stickyBar] - Sticky bar element when already known (avoids redundant lookup)
   */
  #updateDialogPosition(stickyBar) {
    const bar = stickyBar ?? /** @type {HTMLElement | null} */ (this.closest('product-sticky-details'));
    if (!bar || !this.dialog) return;

    const stickyHeight = bar.offsetHeight;
    this.dialog.style.setProperty('--sticky-bar-height', `${stickyHeight}px`);

    // Position dialog aligned with the parent variant-select-dialog element
    const parentRect = this.getBoundingClientRect();
    this.dialog.style.setProperty('--dialog-left', `${parentRect.left}px`);
  }

  /**
   * Binds the resize event listener
   */
  #bindResize() {
    if (this.#debouncedResize) {
      window.addEventListener('resize', this.#debouncedResize);
    }
  }

  /**
   * Unbinds the resize event listener
   */
  #unbindResize() {
    if (this.#debouncedResize) {
      window.removeEventListener('resize', this.#debouncedResize);
    }
  }

  #bindResizeClose() {
    if (this.#debouncedCloseOnResize) {
      window.addEventListener('resize', this.#debouncedCloseOnResize);
    }
  }

  #unbindResizeClose() {
    if (this.#debouncedCloseOnResize) {
      window.removeEventListener('resize', this.#debouncedCloseOnResize);
    }
  }

  /**
   * Resets sticky bar positioning
   */
  #resetStickyBarPosition() {
    if (this.dialog) {
      delete this.dialog.dataset.insideStickyBar;
    }
    this.#unbindResize();
  }

  /**
   * Handles option click events delegated from the dialog content.
   * We do not block clicks on unavailable options (aria-disabled): the regular variant picker
   * (fieldset/radio) also allows selecting unavailable variants so the form can show sold-out state.
   * Keeping this consistent across both picker types.
   * @param {MouseEvent} event
   */
  #handleOptionClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const button = target.closest('[data-option-value]');
    if (!button || !(button instanceof HTMLElement)) return;

    const value = button.dataset.optionValue;
    const swatchColor = button.dataset.swatchColor;
    const swatchImage = button.dataset.swatchImage;
    const variantImage = button.dataset.variantImage;

    if (!value) return;

    const select = this.hiddenSelect;
    if (select) {
      this.#setSelectValue(select, value);
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Update selected state visually in dialog
    this.#updateSelectedVisual(value);

    // Update trigger display
    this.#updateTrigger(value, swatchColor, swatchImage, variantImage);

    // Close the dialog
    this.closeDialog();
  };

  /**
   * Handles clicking on the backdrop to close the dialog
   * @param {MouseEvent} event
   */
  #handleCloseClick = () => {
    this.closeDialog();
  };

  #handleBackdropClick = (event) => {
    const dialog = this.dialog;
    if (!dialog) return;

    // Check if click was on the backdrop (dialog element itself, not its children)
    const rect = dialog.getBoundingClientRect();
    const isInDialog =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;

    // If click is on the ::backdrop (outside dialog content), close
    if (event.target === dialog && !isInDialog) {
      this.closeDialog();
    }
  };

  /**
   * Updates the visual selected state of options in the dialog
   * @param {string} value - The selected value
   */
  #updateSelectedVisual(value) {
    const allOptions = this.querySelectorAll('[data-option-value]');
    for (const opt of allOptions) {
      const isSelected = opt.getAttribute('data-option-value') === value;
      opt.classList.toggle('is-selected', isSelected);
      opt.setAttribute('aria-selected', String(isSelected));
    }
  }

  /**
   * Updates the trigger button display
   * @param {string} value - The selected value text
   * @param {string} [swatchColor] - The swatch color
   * @param {string} [swatchImage] - The swatch image URL
   * @param {string} [variantImage] - The variant image URL
   */
  #updateTrigger(value, swatchColor, swatchImage, variantImage) {
    // Update trigger text
    if (this.triggerValue) {
      this.triggerValue.textContent = value;
    }

    const swatchEl = this.triggerSwatch?.querySelector('.swatch');
    if (swatchEl instanceof HTMLElement) {
      applySwatchBackground(swatchEl, { swatchImage, swatchColor, variantImage });
    }
  }

  /**
   * @param {HTMLSelectElement} select
   * @param {string} value
   */
  #setSelectValue(select, value) {
    for (const opt of select.options) {
      opt.removeAttribute('selected');
      if (opt.value === value) {
        opt.selected = true;
        opt.setAttribute('selected', 'selected');
      }
    }
  }

  /**
   * Syncs the dialog selection state with a given value.
   * Called by variant-picker.js when syncing across components.
   * @param {string} value - The value to select
   */
  syncSelection(value) {
    const select = this.hiddenSelect;
    if (select) {
      this.#setSelectValue(select, value);
    }

    // Update visual state in dialog
    this.#updateSelectedVisual(value);

    // Update trigger display - get swatch data from dialog button
    const selectedButton = this.querySelector(`[data-option-value="${CSS.escape(value)}"]`);
    if (selectedButton instanceof HTMLElement) {
      const { swatchColor, swatchImage, variantImage } = selectedButton.dataset;
      this.#updateTrigger(value, swatchColor, swatchImage, variantImage);
    } else {
      // Fallback: just update text without swatch
      this.#updateTrigger(value);
    }
  }
}

if (!customElements.get('variant-select-dialog')) {
  customElements.define('variant-select-dialog', VariantSelectDialog);
}
